import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ClockIcon, StarIcon, TrophyIcon } from 'lucide-react';
import { CHECKOUT_URL, PRICE_FULL, PRICE_SHORT, Section, trackCheckoutClick } from './Shared';
import { DnaHelix, Mandala } from './BioDecor';
import { pad, useCountdown } from '../hooks/useCountdown';
function TimeBox({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-164" data-mp="1wlr8u0awwxpl">
      <div className="min-w-[58px] rounded-xl bg-blue-900 px-3 py-2 font-mono text-2xl font-bold tabular-nums text-amber-200 md:min-w-[80px] md:px-5 md:py-3 md:text-4xl" data-id="element-165" data-mp="1wn2cm04cy5gp">
        {pad(value)}
      </div>
      <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 md:text-sm" data-id="element-166" data-mp="1mt28o1ktb69k">
        {label}
      </span>
    </div>;
}
function LaunchUrgency() {
  const timeLeft = useCountdown();
  const spotsTaken = 75;
  return <div className="mx-auto mb-12 max-w-4xl" data-id="element-167" data-mp="2kbc51kw2trgt">
      <div className="rounded-3xl border-2 border-amber-500/40 bg-white p-6 shadow-sm md:p-8" data-id="element-168" data-mp="2kcddripme1vd">
        <div className="mb-5 flex items-center justify-center gap-2" data-id="element-169" data-mp="12pevpoh5ujd0">
          <ClockIcon className="h-5 w-5 text-amber-700" data-id="element-170" data-mp="yr7aohfnyvw0" />
          <h3 className="text-base font-bold text-blue-900 md:text-xl" data-id="element-171" data-mp="1ch147d2464c2">
            LAUNCH PRICE ENDS IN:
          </h3>
        </div>
        <div className="mb-5 flex items-center justify-center gap-2 md:gap-4" data-id="element-172" data-mp="17psro85hm2uo">
          <TimeBox value={timeLeft.h} label="Hours" data-id="element-173" data-mp="1mepan09310a2" />
          <span className="pb-5 text-3xl font-bold text-amber-500" data-id="element-174" data-mp="1mhl70k5a7gwe">:</span>
          <TimeBox value={timeLeft.m} label="Minutes" data-id="element-175" data-mp="1hfvuf02euv67" />
          <span className="pb-5 text-3xl font-bold text-amber-500" data-id="element-176" data-mp="1vgos1to0e6dj">:</span>
          <TimeBox value={timeLeft.s} label="Seconds" data-id="element-177" data-mp="1vdssy20fdbni" />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5" data-id="element-178" data-mp="1qhlko2kk3o2e">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900 md:text-base" data-id="element-179" data-mp="12x9g4npcc5y">
            <span className="relative flex h-2.5 w-2.5" data-id="element-180" data-mp="3s9qi1tktrdki">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" data-id="element-181" data-mp="1w5ka3txxjild" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" data-id="element-182" data-mp="213lsecnj7dvs" />
            </span>
            <span data-id="element-183" data-mp="262ovvsnv5c1n"><strong className="text-emerald-700" data-id="element-184" data-mp="262oy7zbc5o4t">{spotsTaken}%</strong> of spots claimed</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-185" data-mp="2a6mum29a0ku6">
            <div className="h-full rounded-full bg-emerald-700" style={{
            width: `${spotsTaken}%`
          }} data-id="element-186" data-mp="2f3d9wnd9bcej" />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-id="element-187" data-mp="2otzivyqgx4o9">
          <AlertTriangleIcon className="h-5 w-5 shrink-0 text-amber-700" data-id="element-188" data-mp="2ttc7anwzmjsy" />
          <p className="text-center text-sm font-semibold text-slate-900 md:text-base" data-id="element-189" data-mp="2xuxqer047ubl">
            When the timer reaches zero, the launch offer ends.
          </p>
        </div>
      </div>
    </div>;
}
function Pricing() {
  return <Section id="comprar" className="my-12 rounded-3xl border border-amber-500/40 bg-stone-100" data-id="element-190" data-mp="r6nk34b7ekid">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" data-id="element-191" data-mp="m716gtrgr291">
        <Mandala className="absolute left-6 top-8 h-20 w-20 text-blue-100" data-id="element-192" data-mp="i6qvf2otdlnb" />
        <DnaHelix className="absolute bottom-6 right-8 hidden h-40 w-20 text-emerald-500/25 md:block" data-id="element-193" data-mp="d75qmbmyqj2o" />
      </div>

      <div className="relative mb-10 text-center" data-id="element-194" data-mp="19vtvs7dkybuo">
        <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-900 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-200 md:text-sm" data-id="element-195" data-mp="19ypuhxx954k6">
          🧬 Launch offer
        </span>
        <h2 className="text-3xl font-bold text-blue-900 md:text-4xl" data-id="element-196" data-mp="w5qgk0d4bieq">
          <span className="text-amber-700" data-id="element-197" data-mp="w3e2vzee7u7h">BIODECODING PRACTITIONER KIT™</span>
        </h2>
        <p className="mt-3 text-sm font-semibold text-emerald-700 md:text-base" data-id="element-198" data-mp="1tm73xxqar7at">
          3 Core Volumes + 5 Bonuses · 245+ Pages
        </p>
      </div>

      <div className="relative mx-auto max-w-2xl space-y-5" data-id="element-199" data-mp="18edgg8yj4496">
        <div className="rounded-3xl bg-white p-6 shadow-lg ring-2 ring-amber-500 md:p-8" data-id="element-200" data-mp="18gzm231v09or">
          <div className="mb-3 flex items-center gap-1.5 text-amber-700" data-id="element-201" data-mp="1dbn35guja1tb">
            <TrophyIcon className="h-4 w-4 fill-current" data-id="element-202" data-mp="zfjb9u74u5k0" />
            <StarIcon className="h-4 w-4 fill-current" data-id="element-203" data-mp="14cjhq67w9abn" />
            <span className="text-[10px] font-bold uppercase tracking-wider md:text-sm" data-id="element-204" data-mp="14f5pa5usbptv">
              Complete practitioner kit
            </span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-205" data-mp="1w365rmknbdut">
            <div className="max-w-md" data-id="element-206" data-mp="1idcg521bgpk1">
              <h3 className="text-lg font-bold text-blue-900 md:text-xl" data-id="element-207" data-mp="1icb34s0qpszo">
                🏆 BIODECODING PRACTITIONER KIT™
              </h3>
              <p className="mt-1 text-sm text-slate-500" data-id="element-208" data-mp="2b1i37vfueqn1">3 Core Volumes + 5 Bonuses · 245+ Pages</p>
              <p className="mt-2 text-sm font-medium leading-relaxed text-emerald-700" data-id="element-209" data-mp="2fs86n34pkjf3">
                The complete practitioner system of session frameworks, guided questions, symbolic integration
                practices, ethical communication tools, and post-session support resources.
              </p>
            </div>
            <div className="shrink-0 text-right" data-id="element-210" data-mp="2yx37eur8pdlv">
              <p className="text-xs uppercase tracking-wide text-slate-500" data-id="element-211" data-mp="2yyxsc92ih5py">Regular Price</p>
              <p className="text-lg text-slate-500 line-through" data-id="element-212" data-mp="2tyifdim5608i">{PRICE_FULL}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-slate-500" data-id="element-213" data-mp="268o6td8uuxu2">Launch Price</p>
              <p className="text-4xl font-bold text-emerald-700 md:text-5xl" data-id="element-214" data-mp="227vto8wv48si">{PRICE_SHORT}</p>
              <span className="mt-1 inline-block rounded-full bg-amber-400 px-2.5 py-0.5 text-xs font-bold text-blue-950" data-id="element-215" data-mp="2g74zcenfzki8">50% OFF</span>
              <p className="mt-1 text-xs text-slate-500" data-id="element-216" data-mp="2b9lnfle7vjtg">One-time payment · Immediate lifetime access</p>
            </div>
          </div>
        </div>

        <motion.a href={CHECKOUT_URL} onClick={trackCheckoutClick} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} transition={{
        duration: 0.14,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex w-full justify-center rounded-full bg-blue-900 px-6 py-5 text-center text-base font-bold text-white shadow-lg transition-colors duration-150 ease-out hover:bg-blue-950 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-400/50 md:text-xl" data-id="element-217" data-mp="3mlcppmp8vj89">
          → GET THE BIODECODING PRACTITIONER KIT FOR {PRICE_SHORT}
        </motion.a>

        <div className="grid gap-3 rounded-2xl bg-emerald-50 p-4 text-center sm:grid-cols-4" data-id="element-218" data-mp="3fxftrsc43fbv">
          {['🛡️ 7-Day Guarantee', '📥 Immediate Digital Download', '🔒 Secure Checkout', '💻 Print-Ready and Tablet-Friendly PDFs'].map(item => <span key={item} className="text-xs font-bold text-emerald-700" data-id="element-219" data-mp="3pplkkyqmnqph">
                {item}
              </span>)}
        </div>
      </div>
    </Section>;
}
export function LaunchOffer() {
  return <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-220" data-mp="2x654tgpy5cmm">
      <LaunchUrgency data-id="element-221" data-mp="2pogqrfbkx0wh" />
      <Pricing data-id="element-222" data-mp="2pmvxdt103o07" />
    </div>;
}